import {
  listeAttenteSchema,
  normaliserEmail,
  type Produit,
  SEUIL_ACTIVATION,
} from "../../../shared/src/app/concerts";

/**
 * Client des mesures et de la fausse porte (ticket 4, issue #316 ; spec
 * docs/idees/concerts/05-spec.md, section 3.3). La page l'appelle ; lui ne leve jamais : une
 * mesure perdue ne casse pas l'atelier d'affiche.
 *
 * - Cookie d'attribution : `credentials: "same-origin"`. `GET /api/visite` pose `lab_src`
 *   (identifiant aleatoire et campagne, premiere partie, decrit dans la politique de
 *   confidentialite du template ; spec, section 7), et les `POST` le renvoient : clics et
 *   inscriptions restent rattaches a la session et a sa source. La source d'une visite voyage
 *   dans l'adresse de `GET /api/visite`, ou ce client recopie les `utm_*` de la page.
 * - Rien de la liste de groupes ne part, sauf leur nombre (`activation`).
 * - L'adresse de la liste d'attente part seulement vers `POST /api/liste-attente`.
 */

/** Indicateur local : l'activation de ce navigateur est deja comptee. */
export const CLE_ACTIVATION = "concerts.activation-envoyee";

/** Raisons d'un refus de la liste d'attente, a traduire en message par la page. */
export type ErreurListeAttente =
  | "consentement"
  | "email"
  | "invalide"
  | "trop-de-requetes"
  | "indisponible"
  | "reseau";

export type ResultatListeAttente = { ok: boolean; erreur?: ErreurListeAttente };

export type DemandeListeAttente = {
  email: string;
  product: Produit;
  consent: boolean;
  /** Valeur du champ piege, cache a l'humain : vide pour une vraie personne. */
  piege?: string;
};

const UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

async function appeler(chemin: string, init: RequestInit = {}): Promise<Response | null> {
  try {
    return await fetch(chemin, { ...init, credentials: "same-origin" });
  } catch {
    return null;
  }
}

function posterJson(chemin: string, corps: unknown): Promise<Response | null> {
  return appeler(chemin, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(corps),
    keepalive: true,
  });
}

function lireLocation(): string {
  try {
    return window.location.search;
  } catch {
    return "";
  }
}

/**
 * Compte la visite : `GET /api/visite`, avec les `utm_*` de l'adresse de la page et eux seuls.
 * A appeler une fois au chargement.
 */
export async function visite(): Promise<void> {
  const page = new URLSearchParams(lireLocation());
  const envoi = new URLSearchParams();
  for (const cle of UTM) {
    const valeur = page.get(cle);
    if (valeur) envoi.set(cle, valeur.slice(0, 200));
  }
  const requete = envoi.toString();
  await appeler(requete ? `/api/visite?${requete}` : "/api/visite");
}

/** Activation confirmee par le serveur dans cette page (repli quand `localStorage` refuse). */
let activationEnvoyeeIci = false;
/** Envoi en cours : deux appels rapproches ne partent pas deux fois. */
let activationEnCours = false;

function activationDejaEnvoyee(): boolean {
  if (activationEnvoyeeIci) return true;
  try {
    return window.localStorage.getItem(CLE_ACTIVATION) !== null;
  } catch {
    return false;
  }
}

function retenirActivation(): void {
  activationEnvoyeeIci = true;
  try {
    window.localStorage.setItem(CLE_ACTIVATION, new Date().toISOString());
  } catch {
    // Stockage refuse : le repli en memoire evite au moins un doublon dans cette page.
  }
}

/**
 * Pose l'activation quand la liste atteint dix groupes, une seule fois par navigateur. Sous le
 * seuil, deja confirmee ou en cours d'envoi, ne fait rien. L'indicateur local n'est ecrit
 * qu'apres une reponse 2xx : un envoi perdu (reseau coupe, 429, panne) sera retente au
 * prochain appel. Rend `true` quand le serveur a pris l'evenement.
 */
export async function activation(count: number): Promise<boolean> {
  if (
    !Number.isInteger(count) ||
    count < SEUIL_ACTIVATION ||
    activationEnCours ||
    activationDejaEnvoyee()
  ) {
    return false;
  }
  activationEnCours = true;
  try {
    const reponse = await posterJson("/api/evenements", {
      type: "activation",
      count: Math.min(count, 300),
    });
    if (reponse?.ok !== true) return false;
    retenirActivation();
    return true;
  } finally {
    activationEnCours = false;
  }
}

/** Pose le clic sur un prix (fausse porte : rien n'est vendu). */
export async function checkout(product: Produit): Promise<void> {
  await posterJson("/api/evenements", { type: "checkout", product });
}

/**
 * Inscrit l'adresse sur la liste d'attente. Controle d'abord en local (consentement, adresse),
 * puis envoie. La reponse est la meme pour une adresse neuve ou deja inscrite.
 */
export async function listeAttente(demande: DemandeListeAttente): Promise<ResultatListeAttente> {
  if (demande.consent !== true) return { ok: false, erreur: "consentement" };
  const corps = {
    email: normaliserEmail(demande.email),
    product: demande.product,
    consent: true as const,
    ...(demande.piege ? { piege: demande.piege.slice(0, 200) } : {}),
  };
  const controle = listeAttenteSchema.safeParse(corps);
  if (!controle.success) {
    const surEmail = controle.error.issues.some((issue) => issue.path[0] === "email");
    return { ok: false, erreur: surEmail ? "email" : "invalide" };
  }

  const reponse = await appeler("/api/liste-attente", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(controle.data),
  });
  if (reponse === null) return { ok: false, erreur: "reseau" };
  if (reponse.ok) return { ok: true };
  if (reponse.status === 429) return { ok: false, erreur: "trop-de-requetes" };
  if (reponse.status === 400) return { ok: false, erreur: "invalide" };
  return { ok: false, erreur: "indisponible" };
}

/** Pour les tests : oublie le repli en memoire de l'activation. */
export function oublierActivationPourLesTests(): void {
  activationEnvoyeeIci = false;
  activationEnCours = false;
}
